import { useState } from "react";
import AsyncSelect from "react-select/async";
import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FormDialog } from "@/components/common/Dialog";
import { LoadingOverlay } from "@/components/common/Loading/LoadingOverlay";
import { showSuccessToast } from "@/hooks/use-toast";
import { grantAiUserAccess } from "../api";
import { loadUserOptions } from "@/components/specific/engagement/engagementSelectStyles";
import type { SelectOption } from "@/components/common/Dropdown/types";

export type AddAiUserAccessDialogProps = {
  existingUserIds: string[];
  callBack: () => Promise<void>;
};

export const AddAiUserAccessDialog = ({ existingUserIds, callBack }: AddAiUserAccessDialogProps) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<SelectOption | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleOpenChange = (open: boolean) => {
    setDialogOpen(open);
    setSelectedUser(null);
    setError(null);
  };

  const loadOptions = async (inputValue: string): Promise<SelectOption[]> => {
    const options = await loadUserOptions(inputValue);
    return options.filter((option) => !existingUserIds.includes(option.value));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) {
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await grantAiUserAccess(selectedUser.value);
      showSuccessToast("Access granted successfully.");
      setDialogOpen(false);
      setSelectedUser(null);
      await callBack();
    } catch (err) {
      setError("Failed to grant access.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <LoadingOverlay isVisible={loading} message="Granting access..." />
      <FormDialog
        open={dialogOpen}
        onOpenChange={handleOpenChange}
        triggerText="Add user"
        triggerIcon={faUserPlus}
        title="Grant AI access"
        onSubmit={handleSubmit}
        submitText="Grant access"
        loading={loading}
        disableSubmit={!selectedUser || loading}
      >
        <div className="grid gap-4 py-4">
          <AsyncSelect<SelectOption, false>
            isDisabled={loading}
            loadOptions={loadOptions}
            defaultOptions
            onChange={(option) => setSelectedUser(option)}
            value={selectedUser}
            isClearable
            placeholder="Search by name..."
            noOptionsMessage={({ inputValue }) => (inputValue.length < 2 ? "Type 2+ characters..." : "No users found")}
          />
          {error && <span className="text-red-500 text-sm">{error}</span>}
        </div>
      </FormDialog>
    </>
  );
};
